import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import type { Route, RouteContext } from "../router";
import { centeredBox } from "../van/centered-box";
import {
	button,
	div,
	h1,
	input,
	label,
	option,
	p,
	select,
	span,
} from "../van/van-tags";

export class Sp2CmdSetLoadAlarmPage implements Route {
	#onChangeEnabled(e: Event): void {
		if (!(e.target instanceof HTMLSelectElement)) return;
		this.#enabled = +e.target.value;
	}

	async #onClickSend(): Promise<void> {
		const req = this.#ctx.api.post(
			`/smartplug2s/${this.#reactive.imei}/cmds`,
		);
		req.setJsonBody({
			CmdData: {
				Enabled: this.#enabled,
				Lower: +this.#lower,
				Upper: +this.#upper,
			},
			CmdType: 0xa6,
		});
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#ctx.router.up();
		} else {
			this.#ctx.error.setText(await res.text());
		}
	}

	#onInputLower(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#lower = e.target.value;
		this.#validateRange();
	}

	#onInputUpper(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#upper = e.target.value;
		this.#validateRange();
	}

	#validateRange(): boolean {
		const lower = Number(this.#lower);
		if (!Number.isInteger(lower)) {
			this.#reactive.rangeHelpText = "Lower value must be an integer";
			return false;
		}

		if (lower < 0) {
			this.#reactive.rangeHelpText = "Lower value must be non-negative";
			return false;
		}

		const upper = Number(this.#upper);
		if (!Number.isInteger(upper)) {
			this.#reactive.rangeHelpText = "Upper value must be an integer";
			return false;
		}

		if (upper > 4000) {
			this.#reactive.rangeHelpText =
				"Upper value must not be greater than 4000";
			return false;
		}

		if (lower >= upper) {
			this.#reactive.rangeHelpText =
				"Lower value must be strictly lower than upper value";
			return false;
		}

		this.#reactive.rangeHelpText = "";
		return true;
	}

	async refresh(match: Match): Promise<void> {
		this.#reactive.rangeHelpText = "";
		if (!match.data?.imei) throw "missing path param: imei";
		this.#reactive.imei = match.data.imei;
	}

	#ctx: RouteContext;

	#enabled = 0;

	#lower = "0";

	#reactive = reactive({
		rangeHelpText: "",
		imei: "",
	});

	#upper = "4000"; // default is max load of device

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1({ class: "title is-4" }, "SmartPlug2 - Set Load Alarm"),
			p({ class: "subtitle is-6" }, () => `IMEI ${this.#reactive.imei}`),
			div(
				{ class: "field" },
				label({ class: "label" }, "Enabled"),
				div(
					{ class: "control" },
					div(
						{ class: "select" },
						select(
							{
								onchange: (e: Event) =>
									this.#onChangeEnabled(e),
							},
							option({ value: "0" }, "0x00"),
							option({ value: "1" }, "0x01"),
						),
					),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Range"),
				div(
					{ class: "field has-addons" },
					div(
						{ class: "control" },
						input({
							class: "input",
							oninput: (e: Event) => this.#onInputLower(e),
							type: "tel",
							value: "0",
						}),
					),
					div(
						{ class: "control" },
						span({ class: "button is-static" }, "–"),
					),
					div(
						{ class: "control" },
						input({
							class: "input",
							oninput: (e: Event) => this.#onInputUpper(e),
							type: "tel",
							value: "4000",
						}),
					),
					div(
						{ class: "control" },
						span({ class: "button is-static" }, "W"),
					),
				),
				p(
					{
						class: () =>
							this.#reactive.rangeHelpText
								? "help is-danger"
								: "is-hidden",
					},
					() => this.#reactive.rangeHelpText,
				),
			),
			div(
				{ class: "buttons mt-5" },
				button(
					{
						class: "button is-primary",
						onclick: () => this.#onClickSend(),
					},
					"Send command",
				),
				button(
					{
						class: "button is-danger",
						onclick: () => this.#ctx.router.up(),
					},
					"Back",
				),
			),
		);
	}
}
